import React, { useEffect, useState } from 'react';
import { Images, Users, Heart, Download } from 'lucide-react';
import { albumsApi } from '../../api/albums';
import { Album } from '../../types';
import { Skeleton } from '../UI/Skeleton';
import { MachhaCharacter } from '../../lib/machhaAsset';
import styles from './AnalyticsTab.module.css';

type AnalyticsData = Awaited<ReturnType<typeof albumsApi.getAnalytics>>;

function formatRelative(timestamp: string): string {
  const diff = Date.now() - new Date(timestamp).getTime();
  const secs = Math.floor(diff / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(timestamp).toLocaleDateString();
}

function StatCard({
  label,
  value,
  icon: Icon,
  color,
}: {
  label: string;
  value: number;
  icon: React.ElementType;
  color: string;
}) {
  return (
    <div className={styles['stat-card']}>
      <div
        className={styles['stat-icon-wrapper']}
        style={{ backgroundColor: `${color}15` }}
      >
        <Icon style={{ width: 16, height: 16, color }} />
      </div>
      <span className={styles['stat-value']}>
        {value.toLocaleString()}
      </span>
      <span className={styles['stat-label']}>
        {label}
      </span>
    </div>
  );
}

function PhotosByDayChart({ data }: { data: AnalyticsData['photosByDay'] }) {
  const maxCount = Math.max(...data.map((d) => d.count), 1);

  return (
    <div className={styles['chart-wrapper']}>
      <h3 className={styles['section-heading']}>
        Photos per Day
      </h3>
      <div className={styles['chart-bars']}>
        {data.map((d) => {
          const pct = maxCount > 0 ? (d.count / maxCount) * 100 : 0;
          const dayLabel = new Date(d.date + 'T00:00:00').toLocaleDateString(undefined, {
            weekday: 'narrow',
          });
          return (
            <div
              key={d.date}
              className={styles['chart-bar-col']}
              title={`${d.date}: ${d.count} photo${d.count !== 1 ? 's' : ''}`}
            >
              <span className={styles['chart-bar-count']}>
                {d.count > 0 ? d.count : ''}
              </span>
              <div
                className={styles['chart-bar']}
                style={{ height: `${Math.max(pct, 2)}%`, minHeight: d.count > 0 ? 4 : 2 }}
              />
              <span className={styles['chart-bar-day']}>
                {dayLabel}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TopPhotosRow({ photos }: { photos: AnalyticsData['topPhotos'] }) {
  if (photos.length === 0) {
    return (
      <div className={styles['top-photos-empty']}>
        No liked photos yet
      </div>
    );
  }

  return (
    <div className={styles['top-photos-wrapper']}>
      <h3 className={styles['section-heading']}>
        Top Photos
      </h3>
      <div className={styles['top-photos-scroll']}>
        {photos.map((photo) => (
          <div
            key={photo.photoId}
            className={styles['top-photo-item']}
          >
            <div className={styles['top-photo-thumb-wrapper']}>
              <img
                src={photo.thumbnailUrl}
                alt={photo.filename}
                className={styles['top-photo-img']}
                loading="lazy"
              />
              <div className={styles['top-photo-overlay']}>
                <div className={styles['top-photo-likes']}>
                  <Heart className={styles['top-photo-heart-icon']} style={{ width: 10, height: 10, fill: 'currentColor' }} />
                  <span className={styles['top-photo-like-count']}>
                    {photo.likeCount}
                  </span>
                </div>
              </div>
            </div>
            <span className={styles['top-photo-filename']}>
              {photo.filename}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function RecentActivityList({ activity }: { activity: AnalyticsData['recentActivity'] }) {
  if (activity.length === 0) {
    return (
      <div className={styles['activity-empty']}>
        No recent activity
      </div>
    );
  }

  const typeConfig: Record<string, { label: string; styleKey: string }> = {
    like: { label: 'liked a photo', styleKey: 'activity-type-like' },
    join: { label: 'joined the album', styleKey: 'activity-type-join' },
    download: { label: 'downloaded photos', styleKey: 'activity-type-download' },
  };

  return (
    <div className={styles['activity-wrapper']}>
      <h3 className={styles['section-heading']}>
        Recent Activity
      </h3>
      <div className={styles['activity-list']}>
        {activity.map((item, i) => {
          const cfg = typeConfig[item.type];
          return (
            <div
              key={`${item.type}-${item.userId}-${item.timestamp}-${i}`}
              className={styles['activity-item']}
            >
              <div className={styles['activity-info']}>
                <span className={styles['activity-user']}>
                  {item.userName}
                </span>
                <span className={[styles['activity-type'], styles[cfg.styleKey]].join(' ')}>
                  {cfg.label}
                </span>
              </div>
              <span className={styles['activity-time']}>
                {formatRelative(item.timestamp)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function AnalyticsTab({ album }: { album: Album }) {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(false);
    albumsApi
      .getAnalytics(album._id)
      .then(setData)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [album._id]);

  if (loading) {
    return (
      <div className={styles['skeleton-wrapper']}>
        <div className={styles['skeleton-grid']}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className={styles['skeleton-stat']} />
          ))}
        </div>
        <Skeleton className={styles['skeleton-chart']} />
        <Skeleton className={styles['skeleton-row']} />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className={styles['error-state']}>
        <MachhaCharacter state="Error" size={80} />
        <p className={styles['error-text']}>
          Failed to load analytics
        </p>
      </div>
    );
  }

  return (
    <div className={styles['analytics-container']}>
      {/* Stat cards */}
      <div className={styles['stat-grid']}>
        <StatCard label="Photos" value={data.totalPhotos} icon={Images} color="var(--clay-indigo, #ef4444)" />
        <StatCard label="Members" value={data.totalMembers} icon={Users} color="var(--clay-orange, #f97316)" />
        <StatCard label="Likes" value={data.totalLikes} icon={Heart} color="var(--clay-rose, #f43f5e)" />
        <StatCard label="Downloads" value={data.totalDownloads} icon={Download} color="#10b981" />
      </div>

      {/* Top photos */}
      <TopPhotosRow photos={data.topPhotos} />

      {/* Photos per day chart */}
      <PhotosByDayChart data={data.photosByDay} />

      {/* Recent activity */}
      <RecentActivityList activity={data.recentActivity} />
    </div>
  );
}
